import { useCallback } from 'react';
import { queryClient } from 'utils/query';

import {
    DataSource,
    DataSourceQuery,
    DataSourceQueryResult,
    firstPage,
} from './data_source';
import { buildPagedQueryKey } from './usePagedDataSource';

/**
 * Returns `prefetch` and `setData` utilities for a paged data source.
 *
 * Both functions target page 1 using the data source's current sort and page
 * size — matching what the next navigation would fetch.
 *
 * @param dataSource The paged data source to prefetch for.
 * @param queryFn The same query function used by the data source.
 *
 * @example
 * const { prefetch, setData } = usePagedPrefetch(dataSource, getDirectoryEntries);
 *
 * // On hover — fetch and cache if not already fresh:
 * prefetch(nextFolderFilter);
 *
 * // If you already have the data:
 * setData(nextFolderFilter, alreadyFetchedData);
 */
export function usePagedPrefetch<TData, TFilter>(
    dataSource: DataSource<TData, TFilter>,
    queryFn: DataSourceQuery<TData, TFilter>,
) {
    const {
        name,
        sort,
        filter: baseFilter,
        paging: { pageLimit },
        staleTime,
    } = dataSource;

    const prefetch = useCallback(
        (filterOverrides: TFilter) => {
            const combinedFilter = { ...baseFilter, ...filterOverrides };
            return queryClient.prefetchQuery({
                queryKey: buildPagedQueryKey(
                    name,
                    combinedFilter,
                    sort,
                    firstPage,
                    pageLimit,
                ),
                queryFn: () =>
                    queryFn({ filter: combinedFilter, sort, page: firstPage, pageLimit }),
                staleTime,
            });
        },
        [baseFilter, name, sort, pageLimit, staleTime, queryFn],
    );

    const setData = useCallback(
        (filter: TFilter, data: DataSourceQueryResult<TData>) => {
            queryClient.setQueryData(
                buildPagedQueryKey(name, filter, sort, firstPage, pageLimit),
                data,
            );
        },
        [name, sort, pageLimit],
    );

    return { prefetch, setData };
}
